import { X } from 'lucide-react';
import { createContext, useContext, useId, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useScreen } from '../lib/layout.ts';
import { focusables, useModalFocus } from '../lib/modal-focus.ts';
import { cn } from '../lib/utils.ts';
import { Button } from './ui/button.tsx';
import { Icon } from './ui/icon.tsx';

/**
 * Knopfzeile mit Hauptknopf in einem Formular, das am Handy im Blatt steht: bleibt unten im Blatt stehen, während der
 * Inhalt scrollt (EXPERIENCE §10.2 „Hauptknopf unten“). Gilt nur unter 768 px (dort sind Popovers Blätter).
 */
export const SHEET_ACTIONS = 'max-tablet:sticky max-tablet:bottom-0 max-tablet:z-[var(--z-1)] max-tablet:-mx-4 max-tablet:bg-card max-tablet:px-4 max-tablet:py-3';

/** Steht der Inhalt in einem Blatt? (Karten-Formular: Leiste „‹Karte› geändert“ dann im Blatt statt über der Seite.) */
const InSheetContext = createContext(false);

export function useInSheet(): boolean {
  return useContext(InSheetContext);
}

/**
 * Blatt von unten (Sheet, EXPERIENCE §10.2 „Dialog, Popover – Handy: als Blatt von unten“; DESIGN Do's: „Dialoge als Blatt
 * von unten“): weiße Karte am unteren Rand über die volle Breite, oben gerundet, Seite dahinter mit `{colors.scrim}`
 * abgedunkelt. Fokus beim Öffnen hinein und gefangen; Esc, ✕ oder Tippen daneben schließt, danach Fokus zurück auf den
 * auslösenden Knopf (`opener`). Liegt im Portal am Ende von `body`, damit nichts es abschneidet.
 */
export function Sheet({
  label,
  opener,
  onClose,
  children,
  id,
  initialFocus,
  className,
}: {
  /** Name für Hilfsmittel und Überschrift des Blatts. */
  label: string;
  opener: RefObject<HTMLElement | null>;
  onClose: () => void;
  children: ReactNode;
  id?: string;
  initialFocus?: () => HTMLElement | null;
  className?: string;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  // Fokus wie beim Popover in das erste Feld bzw. den ersten Knopf des Inhalts (nicht auf ✕).
  useModalFocus(panel, opener, onClose, initialFocus ?? (() => (content.current ? (focusables(content.current)[0] ?? null) : null)));
  return createPortal(
    <>
      <div data-testid="blatt-abdunklung" aria-hidden className="fixed inset-0 z-[var(--z-50)] bg-scrim" onClick={onClose} />
      <div
        id={id}
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        data-testid="blatt"
        className={cn(
          'fixed inset-x-0 bottom-0 z-[var(--z-50)] flex max-h-[85vh] animate-sheet-in flex-col rounded-t-card bg-card text-ink shadow-float motion-reduce:animate-none',
          className,
        )}
      >
        <div className="flex flex-none items-center justify-between gap-3 pt-2 pr-2 pl-4">
          <span className="type-card-title min-w-0">{label}</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="grid size-11 flex-none cursor-pointer place-items-center rounded-full text-ink-2"
          >
            <Icon icon={X} size="lg" aria-hidden />
          </button>
        </div>
        <div ref={content} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pt-1 pb-[max(16px,env(safe-area-inset-bottom))]">
          <InSheetContext.Provider value={true}>{children}</InSheetContext.Provider>
        </div>
      </div>
    </>,
    document.body,
  );
}

interface ConfirmPanelBase {
  /** Zusammenfassung: Titel des Blatts; ohne `confirmLabel` auch Text des Hauptknopfs in der Zeile. */
  title: string;
  hint?: ReactNode;
  confirmLabel?: string;
  /** Hauptknopf im Blatt; ohne Angabe wie in der Zeile. */
  phoneConfirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  pending?: boolean;
  /** Fehler der Aktion (nur im Blatt; in der Zeile zeigt ihn die Seite wie bisher). */
  error?: string | null;
  opener?: RefObject<HTMLElement | null>;
  className?: string;
  /** Story 5.98: `danger` = roter Gefahr-Knopf (DESIGN „Gefahr“) für Endgültiges, z. B. „Endgültig löschen“. */
  confirmVariant?: 'primary' | 'danger';
}

/** Entweder eigene Darstellung in der Zeile (`inline`) oder Standard-Kachel mit Block, Sperre und Beschreibung. */
type ConfirmPanelExtra =
  | {
      /** Eigene Darstellung in der Zeile (Tablet/Desktop) statt der Standard-Kachel. */
      inline: ReactNode;
      details?: never;
      confirmDisabled?: never;
      describedBy?: never;
    }
  | {
      inline?: undefined;
      /** Block zum Lesen vor dem Bestätigen (volle Breite). */
      details?: ReactNode;
      /** Hauptknopf aus (zusätzlich zu `pending`). */
      confirmDisabled?: boolean;
      /** Weitere Ids für `aria-describedby` des Hauptknopfs (neben dem Satz). */
      describedBy?: string;
    };

/**
 * Echte Bestätigung (EXPERIENCE §6.4: Knopf mit Zusammenfassung, Hinweis daneben, kein zusätzliches Fenster), am Handy als
 * Blatt von unten (§10.2 „Dialog – Handy: als Blatt von unten“; Story 1.24).
 * - Tablet und Desktop: wie bisher eine Kachel in der Zeile – Hauptknopf mit Zusammenfassung, Satz, „Abbrechen“ (oder
 *   eine eigene Darstellung über `inline`).
 * - Handy: Blatt mit der Zusammenfassung als Titel, dem Satz, Hauptknopf (`phoneConfirmLabel`, z. B. „Umschalten“) und
 *   „Abbrechen“; ein Fehler steht im Blatt. Esc, ✕ oder Tippen daneben bricht ab; danach Fokus zurück auf das Element, das
 *   beim Öffnen den Fokus hatte (bzw. `opener`).
 * - `details` (Story 9.13): Block über die volle Breite, den man vor dem Bestätigen lesen soll (z. B. der Text einer
 *   E-Mail); in der Zeile über den Knöpfen, im Blatt unter dem Satz. `confirmDisabled` hält den Hauptknopf aus (z. B. bis
 *   der Block geladen ist); `describedBy` nennt dem Hauptknopf zusätzlich zum Satz weitere Beschreibungen (z. B. die Id
 *   des Blocks). Mit `inline` gibt es die Standard-Kachel nicht – darum schließt der Typ diese drei dann aus.
 */
export function ConfirmPanel({
  title,
  hint,
  confirmLabel,
  phoneConfirmLabel,
  onConfirm,
  onCancel,
  pending = false,
  error,
  opener,
  inline,
  details,
  confirmDisabled = false,
  describedBy,
  className,
  confirmVariant = 'primary',
}: ConfirmPanelBase & ConfirmPanelExtra) {
  const screen = useScreen();
  const hintId = useId();
  const describedByIds = [hint ? hintId : null, describedBy ?? null].filter((x) => x !== null).join(' ') || undefined;
  // Element mit dem Fokus beim Öffnen (z. B. der gewählte Reiter des Segments): dorthin kehrt der Fokus zurück.
  // iOS fokussiert getippte Knöpfe nicht (dann steht `body` da): dann bewegt das Schließen den Fokus nicht. Ein inzwischen
  // entfernter Knopf wird beim Schließen übergangen (`useModalFocus` prüft `isConnected`).
  const focusedAtOpen = useRef<HTMLElement | null>(
    typeof document === 'undefined' || document.activeElement === document.body ? null : (document.activeElement as HTMLElement | null),
  );
  if (screen !== 'phone') {
    if (inline !== undefined) return <>{inline}</>;
    return (
      <div className={cn('flex flex-wrap items-center gap-4 rounded-tile bg-tile px-4 py-3', className)}>
        {details !== undefined ? <div className="min-w-0 basis-full">{details}</div> : null}
        <Button variant={confirmVariant} aria-describedby={describedByIds} disabled={pending || confirmDisabled} onClick={onConfirm}>
          {confirmLabel ?? title}
        </Button>
        {hint ? (
          <span id={hintId} className="type-meta text-ink">
            {hint}
          </span>
        ) : null}
        <Button variant="secondary" disabled={pending} onClick={onCancel}>
          Abbrechen
        </Button>
      </div>
    );
  }
  return (
    // Während die Aktion läuft, schließen Esc, ✕ und Tippen daneben nicht: sonst ginge sie unbemerkt weiter.
    <Sheet label={title} opener={opener ?? focusedAtOpen} onClose={pending ? () => {} : onCancel}>
      <div className="flex flex-col gap-4">
        {hint ? (
          <p id={hintId} className="text-row text-ink">
            {hint}
          </p>
        ) : null}
        {details !== undefined ? <div className="min-w-0">{details}</div> : null}
        {error ? (
          <p role="alert" className="type-meta text-clarify">
            {error}
          </p>
        ) : null}
        <div className={cn('flex flex-col gap-3', SHEET_ACTIONS)}>
          <Button variant={confirmVariant} aria-describedby={describedByIds} disabled={pending || confirmDisabled} onClick={onConfirm} className="w-full">
            {phoneConfirmLabel ?? confirmLabel ?? title}
          </Button>
          <Button variant="secondary" disabled={pending} onClick={onCancel} className="w-full">
            Abbrechen
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
